<template>
  <section class="financial__review financial__panel" aria-labelledby="financial-review-title">
    <header class="financial__detail-head">
      <button class="financial__back-link" type="button" @click="close">
        <mdicon name="arrow-left" :size="18" aria-hidden="true" /> {{ backLabel }}
      </button>
      <h1 id="financial-review-title" tabindex="-1">{{ row.trackingNumber || 'Charge without shipment number' }}</h1>
      <!-- The carrier, the customer and the charge with its role, in one line under the shipment number. -->
      <p class="financial__help">
        {{ batch.carrier }} · {{ row.match ? billingName : 'Customer not identified' }} · {{ row.description }}
        <span class="financial__tag">{{ roleLabel }}</span>{{ row.date ? ` · ${row.date}` : '' }}
      </p>
    </header>

    <!-- One line for a clean row; otherwise only what differs, what is missing with its fix, and the checks. -->
    <section
      class="financial__review-summary"
      :class="{ 'financial__review-summary--different': differences.length || blockingWarnings.length }"
      aria-labelledby="financial-summary-title"
    >
      <mdicon :name="differences.length || blockingWarnings.length ? 'alert-circle-outline' : 'information-outline'" aria-hidden="true" />
      <div>
        <h2 id="financial-summary-title" tabindex="-1">{{ summaryTitle }}</h2>
        <dl v-if="differences.length || hasMissing || hasChecks" class="financial__summary-list">
          <div v-if="differences.length">
            <dt>Differs</dt>
            <dd>{{ differsText }}</dd>
          </div>
          <div v-if="hasMissing">
            <dt>Missing</dt>
            <dd>
              <ul class="financial__summary-items">
                <li v-if="!row.match"><span>{{ matchAlert }}</span></li>
                <li v-if="!metadataComplete">
                  <span>Invoice details: {{ missingMetadataList }}</span>
                  <button
                    class="financial__button financial__button--small"
                    type="button"
                    :disabled="busy"
                    @click="editInvoice"
                  >
                    Complete invoice details
                  </button>
                </li>
                <li v-if="mappingMissing">
                  <span>Confirmed service mapping</span>
                  <button
                    class="financial__button financial__button--small"
                    type="button"
                    :disabled="busy"
                    @click="confirmMapping"
                  >
                    Confirm “{{ row.description }}” = {{ row.match?.service }}
                  </button>
                </li>
              </ul>
            </dd>
          </div>
          <div v-if="hasChecks">
            <dt>Checks</dt>
            <dd>
              <ul class="financial__summary-items">
                <li v-if="reopened">
                  <mdicon name="restore" :size="16" aria-hidden="true" /><span>{{ reopened }}</span>
                </li>
                <li v-for="warning in signals.warnings" :key="warning.key">
                  <mdicon
                    :name="warning.blocking ? 'alert-outline' : 'information-outline'"
                    :size="16"
                    aria-hidden="true"
                  />
                  <span><strong>{{ warning.blocking ? 'Check:' : 'Note:' }}</strong> {{ warning.message }}</span>
                </li>
              </ul>
            </dd>
          </div>
        </dl>
      </div>
      <span class="financial__badge" :class="{ 'financial__badge--ready': row.review?.status === 'approved' }">{{ reviewLabel }}</span>
    </section>

    <!-- A check that agrees is one line with its explanation folded; a difference shows both values and why. -->
    <div class="financial__table-wrap financial__comparison-wrap">
      <table class="financial__comparison">
        <caption>Checks</caption>
        <thead>
          <tr>
            <th scope="col">Check</th>
            <th scope="col">Carrier invoice</th>
            <th scope="col">PacSort / agreement</th>
            <th scope="col">Difference</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="comparison in checkRows" :key="comparison.key" :data-state="comparison.state">
            <th scope="row">{{ comparison.label }}</th>
            <td v-if="comparison.state === 'same' || comparison.state === 'information'" colspan="3">
              <details>
                <summary>{{ sameLine(comparison) }}</summary>
                <p>{{ comparison.explanation }}</p>
              </details>
            </td>
            <template v-else>
              <td>{{ comparison.invoice }}</td>
              <td>{{ comparison.pacsort }}</td>
              <td>
                <strong>
                  <mdicon v-if="comparison.state === 'different'" name="alert-outline" :size="16" aria-hidden="true" />
                  {{ comparison.difference }}
                </strong>
                <p>{{ comparison.explanation }}</p>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <section
      v-if="signals.entries.length"
      class="financial__shipment-rows"
      aria-labelledby="financial-review-rows-title"
    >
      <h2 id="financial-review-rows-title">Other invoice rows for this shipment ({{ signals.entries.length }})</h2>
      <div class="financial__table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Invoice</th>
              <th scope="col">Charge</th>
              <th scope="col">Carrier cost</th>
              <th scope="col">Status</th>
              <th scope="col">Customer amount</th>
              <th scope="col"><span class="visually-hidden">Action</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in signals.entries" :key="`${entry.batch.id}:${entry.row.id}`">
              <td>{{ entry.current ? 'This invoice' : invoiceLabel(entry) }}<small>Row {{ entry.row.rowNumber }} · {{ entry.row.date }}</small></td>
              <td>{{ entry.row.description }}<small>{{ roleText(entry.role) }}</small></td>
              <td class="financial__numeric">{{ amountLabel(entry.amount, entry.batch.currency) }}</td>
              <td>{{ entryStatus(entry) }}</td>
              <td class="financial__numeric">{{ amountLabel(entry.row.review?.customerPrice ?? null, entry.batch.currency) }}</td>
              <td>
                <button
                  class="financial__button financial__button--small"
                  type="button"
                  :disabled="busy"
                  :aria-label="openLabel(entry)"
                  @click="openEntry(entry)"
                >
                  Open row
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="financial__decision" aria-labelledby="financial-decision-title">
      <h2 id="financial-decision-title">Decide the customer charge</h2>
      <div
        class="financial__price-grid financial__price-grid--two"
        role="group"
        aria-label="Separate price sources for this row"
      >
        <div>
          <span>Actual carrier cost · this row</span><strong>{{ amountLabel(amount, batch.currency) }}</strong>
        </div>
        <div>
          <span>Quoted shipment price</span><strong>{{ amountLabel(row.match?.bookedPrice ?? null, 'SEK') }}</strong>
          <small>The original shipment quote; not an additional amount for this row.</small>
          <small>{{ booked.explanation }}</small>
        </div>
      </div>
      <div class="financial__price-grid financial__price-grid--two" role="group" aria-label="Customer amount under review">
        <div>
          <span>Reviewed customer amount · this row</span><strong>{{ customerValue === null ? '—' : amountLabel(customerValue, batch.currency) }}</strong>
          <small v-if="signals.role !== 'base'">Customer margin · {{ batch.carrier }}: {{ customerMargin ? `${customerMargin.percent}%` : '—' }}</small>
          <small :class="{ 'financial__negative': marginNegative }">
            <mdicon v-if="marginNegative" name="alert-outline" :size="14" aria-hidden="true" />
            {{ marginText }}
          </small>
        </div>
        <div v-if="purchaseValue !== null">
          <span>Purchase tariff</span><strong>{{ amountLabel(purchaseValue, batch.currency) }}</strong>
          <small v-if="tariffDelta !== null">Cost − tariff {{ tariffDelta }}</small>
        </div>
      </div>
      <form class="financial__review-form" novalidate @submit.prevent="approve">
        <label class="financial__label" for="financial-customer-price">
          Customer amount for this row {{ batch.currency ? `(${batch.currency})` : '' }}
        </label>
        <input
          id="financial-customer-price"
          v-model="customerPrice"
          class="form-control"
          inputmode="decimal"
          autocomplete="off"
          placeholder="Agreed customer amount"
          :disabled="busy"
          :readonly="decided"
          :aria-invalid="!!errors.customerPrice"
          :aria-describedby="
            errors.customerPrice ? 'financial-customer-error' : customerHint ? 'financial-customer-hint' : undefined
          "
          @input="edited('customerPrice')"
        />
        <p v-if="errors.customerPrice" id="financial-customer-error" class="financial__error">
          {{ errors.customerPrice }}
        </p>
        <p v-else-if="customerHint" id="financial-customer-hint" class="financial__field-hint">{{ customerHint }}</p>
        <template v-if="!decided">
          <div class="financial__actions financial__suggestions">
            <button
              v-if="suggestion && !same(customerValue, suggestion.amount)"
              class="financial__button financial__button--small financial__suggestion"
              type="button"
              :disabled="busy"
              @click="useSuggestion"
            >
              Use PacSort price: {{ amountLabel(suggestion.amount, batch.currency) }}
            </button>
            <button
              v-if="
                markupPrice !== null &&
                  !same(markupPrice, suggestion?.amount ?? null) &&
                  !same(customerValue, markupPrice)
              "
              class="financial__button financial__button--small financial__suggestion"
              type="button"
              :disabled="busy"
              @click="useMarkup"
            >
              Use cost + {{ customerMargin!.percent }}% margin: {{ amountLabel(markupPrice, batch.currency) }}
            </button>
          </div>
          <p v-if="suggestion" class="financial__field-hint">PacSort price: {{ suggestion.label }}.</p>
          <p v-else class="financial__field-hint">{{ unpricedText }}</p>
        </template>
        <details class="financial__source" :open="tariffOpen" @toggle="toggleTariff">
          <summary>Purchase tariff (optional)</summary>
          <label class="financial__label" for="financial-purchase-price">
            Tariff from the carrier agreement {{ batch.currency ? `(${batch.currency})` : '' }}
          </label>
          <input
            id="financial-purchase-price"
            v-model="purchasePrice"
            class="form-control"
            inputmode="decimal"
            autocomplete="off"
            :disabled="busy"
            :readonly="decided"
            :aria-invalid="!!errors.purchasePrice"
            :aria-describedby="
              errors.purchasePrice ? 'financial-purchase-error' : purchaseHint ? 'financial-purchase-hint' : undefined
            "
            @input="edited('purchasePrice')"
          />
          <p v-if="errors.purchasePrice" id="financial-purchase-error" class="financial__error">
            {{ errors.purchasePrice }}
          </p>
          <p v-else-if="purchaseHint" id="financial-purchase-hint" class="financial__field-hint">{{ purchaseHint }}</p>
        </details>
        <label class="financial__label" for="financial-review-note">
          {{ noteRequired ? 'Note (required)' : 'Note' }}
        </label>
        <textarea
          id="financial-review-note"
          v-model="note"
          class="form-control"
          rows="2"
          :disabled="busy"
          :readonly="decided"
          :aria-invalid="!!errors.note"
          :aria-describedby="errors.note ? 'financial-note-error' : decided ? undefined : 'financial-note-hint'"
          @input="edited('note')"
        />
        <p v-if="errors.note" id="financial-note-error" class="financial__error">{{ errors.note }}</p>
        <p v-else-if="!decided" id="financial-note-hint" class="financial__field-hint">
          Required to exclude and when something differs
        </p>
        <!-- An approved or excluded row only says who decided; Reopen is its one action. -->
        <div v-if="decided" class="financial__decided">
          <p>{{ lastDecision }}</p>
          <button id="financial-review-reopen" class="financial__button" :disabled="busy" type="button" @click="reopen">
            Reopen review
          </button>
        </div>
        <template v-else>
          <label class="financial__check">
            <input
              v-model="confirmed"
              type="checkbox"
              :disabled="busy"
              :aria-invalid="!!errors.confirmed"
              :aria-describedby="errors.confirmed ? 'financial-confirm-error' : undefined"
              @change="errors.confirmed = ''"
            />
            {{ confirmText }}
          </label>
          <p v-if="errors.confirmed" id="financial-confirm-error" class="financial__error">{{ errors.confirmed }}</p>
          <p v-if="approvalBlocker" class="financial__field-hint financial__blocker">
            <mdicon name="lock-outline" :size="16" aria-hidden="true" /> {{ approvalBlocker }}
          </p>
          <div class="financial__actions">
            <button
              class="financial__button financial__button--primary"
              :disabled="busy || !!approvalBlocker"
              type="submit"
            >
              Approve this charge
            </button>
            <button
              v-if="hasNext"
              class="financial__button"
              :disabled="busy || !!approvalBlocker"
              type="button"
              @click="approveNext"
            >
              Approve and open next
            </button>
            <button
              class="financial__button financial__button--quiet financial__decision-exclude"
              :disabled="busy"
              type="button"
              @click="exclude"
            >
              Exclude with reason
            </button>
          </div>
        </template>
        <p v-if="failure" class="financial__alert financial__alert--error" role="alert">{{ failure }}</p>
        <p v-else-if="notice" class="financial__message" role="status">{{ notice }}</p>
        <p v-if="draftRestored" class="financial__field-hint" role="status">
          Unsaved changes for this row were restored.
          <button class="financial__link" type="button" @click="discardDraft">Discard changes</button>
        </p>
      </form>
      <details v-if="row.review?.history?.length" class="financial__source">
        <summary>Earlier decisions ({{ row.review.history.length }})</summary>
        <ul class="financial__history-list">
          <li v-for="(event, index) in row.review.history" :key="index">
            {{ statusText(event.status) }} by {{ event.userName }} · {{ timeLabel(event.reviewedAt) }}
            <small>
              Customer amount {{ amountLabel(event.customerPrice, batch.currency) }}{{ event.note ? ` · ${event.note}` : '' }}{{ event.reason ? ` · ${event.reason}` : '' }}
            </small>
          </li>
        </ul>
      </details>
    </section>

    <details class="financial__source">
      <summary>Source and shipment details · {{ row.sheet }}, row {{ row.rowNumber }}</summary>
      <dl class="financial__facts">
        <div><dt>Original file</dt><dd>{{ batch.fileName }}</dd></div>
        <div><dt>Invoice row date</dt><dd>{{ row.date || 'Not supplied' }}</dd></div>
        <template v-if="row.match">
          <div><dt>PacSort reference</dt><dd>{{ row.match.itemReference }}</dd></div>
          <div><dt>Assignment</dt><dd>{{ row.match.assignmentReference || '—' }}</dd></div>
          <div><dt>Sender address</dt><dd>{{ row.match.senderAddressName || 'Not loaded' }}</dd></div>
          <div><dt>Booked</dt><dd>{{ row.match.createdAt.slice(0, 10) }}</dd></div>
          <div><dt>Booked dimensions</dt><dd>{{ dimensionsLabel }}</dd></div>
          <div><dt>Terminal scan</dt><dd>{{ row.match.receivedInFacilityAt ? timeLabel(row.match.receivedInFacilityAt) : 'Not scanned at the terminal' }}</dd></div>
        </template>
        <div v-for="(value, key) in row.source" :key="key"><dt>{{ key }}</dt><dd>{{ value || '—' }}</dd></div>
      </dl>
    </details>
  </section>
</template>
<script lang="ts">
import type { FinancialRowWarning } from '@/types/Models/Financial'
import type {
  FinancialCharge,
  FinancialChargeRole,
  FinancialComparison,
  FinancialCustomerMargin,
  FinancialImport,
  FinancialPriceSource,
  FinancialPriceSuggestion,
  FinancialReviewDraft,
  FinancialReviewStatus,
  FinancialReviewSubmission,
  FinancialRowSignals,
  FinancialServiceMapping,
  FinancialShipmentEntry,
} from '@/types/Models/Financial'
import { computed, defineComponent, nextTick, reactive, ref, watch, type ComputedRef, type PropType, type Ref } from 'vue'
import {
  financialAmountLabel,
  financialAmountsEqual,
  financialDecimal,
  financialDifferenceLabel,
  financialPercent,
  subtractFinancialAmounts,
} from '@/services/FinancialAmounts'
import { financialComparisons } from '@/services/FinancialComparison'
import {
  financialBillingName,
  financialChargeAmount,
  financialInvoiceLabel,
  financialMetadataComplete,
  financialMissingMetadata,
  financialReady,
} from '@/services/FinancialImport'
import { financialMarkup } from '@/services/FinancialPricing'

export default defineComponent({
  name: 'FinancialChargeReview',
  props: {
    batch: { type: Object as PropType<FinancialImport>, required: true },
    row: { type: Object as PropType<FinancialCharge>, required: true },
    signals: { type: Object as PropType<FinancialRowSignals>, required: true },
    mappings: { type: Array as PropType<FinancialServiceMapping[]>, default: () => [] },
    draft: { type: Object as PropType<FinancialReviewDraft | null>, default: null },
    busy: { type: Boolean, default: false },
    hasNext: { type: Boolean, default: false },
    backLabel: { type: String, default: 'Back to charges' },
    // PacSort's price for this row, and the customer's margin for this carrier (null when not loaded).
    suggestion: { type: Object as PropType<FinancialPriceSuggestion | null>, default: null },
    customerMargin: { type: Object as PropType<FinancialCustomerMargin | null>, default: null },
    unpricedText: { type: String, default: '' },
    notice: { type: String, default: '' },
    failure: { type: String, default: '' },
  },
  emits: ['close', 'review', 'invoice-details', 'draft', 'confirm-mapping', 'open-entry'],
  setup(props, { emit }) {
    // The same role names as the shipment detail and the customer review.
    const roleNames: Record<FinancialChargeRole, string> = {
      base: 'Delivery',
      additional: 'Additional charge',
      credit: 'Credit',
    }
    const customerPrice: Ref<string> = ref('')
    const purchasePrice: Ref<string> = ref('')
    const note: Ref<string> = ref('')
    const confirmed: Ref<boolean> = ref(false)
    const draftRestored: Ref<boolean> = ref(false)
    // The optional purchase tariff is folded; it opens for a row that has a saved or unsaved tariff.
    const tariffOpen: Ref<boolean> = ref(false)
    const errors = reactive({ customerPrice: '', purchasePrice: '', note: '', confirmed: '' })

    const amount: ComputedRef<string | null> = computed(() => financialChargeAmount(props.batch, props.row))
    const customerValue: ComputedRef<string | null> = computed(() =>
      customerPrice.value.trim() ? financialDecimal(customerPrice.value) : null,
    )
    const purchaseValue: ComputedRef<string | null> = computed(() =>
      purchasePrice.value.trim() ? financialDecimal(purchasePrice.value) : null,
    )
    const metadataComplete: ComputedRef<boolean> = computed(() => financialMetadataComplete(props.batch))
    const listText = (items: string[]): string =>
      items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
    const missingMetadata: ComputedRef<string> = computed(() => listText(financialMissingMetadata(props.batch)))
    const missingMetadataList: ComputedRef<string> = computed(() => financialMissingMetadata(props.batch).join(', '))
    const comparisons: ComputedRef<FinancialComparison[]> = computed(() =>
      financialComparisons(props.batch, props.row, purchasePrice.value.trim() || null, props.mappings),
    )
    const differences: ComputedRef<FinancialComparison[]> = computed(() =>
      comparisons.value.filter((comparison) => comparison.state === 'different'),
    )
    const byKey = (key: FinancialComparison['key']): FinancialComparison =>
      comparisons.value.find((item) => item.key === key)!
    // The booked price is a price tile, not a check row; its explanation is the tile's caption.
    const booked: ComputedRef<FinancialComparison> = computed(() => byKey('booked'))
    // Weight and service always; the amount only once a purchase tariff is entered.
    const checkRows: ComputedRef<FinancialComparison[]> = computed(() => [
      byKey('weight'),
      byKey('service'),
      ...(purchasePrice.value.trim() ? [byKey('price')] : []),
    ])
    // A matched delivery whose carrier product has no confirmed PacSort service yet.
    const mappingMissing: ComputedRef<boolean> = computed(
      () => props.signals.role === 'base' && !!props.row.match?.service && byKey('service').state === 'incomplete',
    )
    const hasMissing: ComputedRef<boolean> = computed(
      () => !props.row.match || !metadataComplete.value || mappingMissing.value,
    )
    const blockingWarnings: ComputedRef<FinancialRowWarning[]> = computed(() => props.signals.warnings.filter((warning) => warning.blocking))
    // An approved or excluded row is read-only until it is reopened.
    const decided: ComputedRef<boolean> = computed(
      () => props.row.review?.status === 'approved' || props.row.review?.status === 'excluded',
    )
    const margin: ComputedRef<string | null> = computed(() =>
      customerValue.value !== null && amount.value !== null ? subtractFinancialAmounts(customerValue.value, amount.value) : null,
    )
    const marginNegative: ComputedRef<boolean> = computed(() => margin.value !== null && Number(margin.value) < 0)
    const signMismatch: ComputedRef<boolean> = computed(
      () =>
        customerValue.value !== null &&
        amount.value !== null &&
        Number(customerValue.value) !== 0 &&
        Math.sign(Number(customerValue.value)) !== Math.sign(Number(amount.value)),
    )
    const noteRequired: ComputedRef<boolean> = computed(
      () =>
        differences.value.length > 0 ||
        blockingWarnings.value.length > 0 ||
        props.signals.role === 'credit' ||
        marginNegative.value ||
        signMismatch.value,
    )
    const tariffDelta: ComputedRef<string | null> = computed(() =>
      purchaseValue.value !== null && amount.value !== null
        ? financialDifferenceLabel(subtractFinancialAmounts(amount.value, purchaseValue.value), props.batch.currency)
        : null,
    )
    // Carrier cost plus the customer's margin: offered when the delivery made a loss or PacSort has no price.
    const markupPrice: ComputedRef<string | null> = computed(() =>
      amount.value !== null && props.customerMargin && (!props.suggestion || props.signals.booked.state === 'different')
        ? financialMarkup(amount.value, props.customerMargin.percent)
        : null,
    )
    // Amounts are compared at öre, so 49 and 49.00 are the same price.
    const same = (left: string | null, right: string | null): boolean =>
      left !== null && right !== null && financialAmountsEqual(left, right, 2)
    const priceSource = (): { priceSource: FinancialPriceSource; marginPercent: number | null } => {
      if (props.suggestion && same(customerValue.value, props.suggestion.amount))
        return { priceSource: props.suggestion.source, marginPercent: props.suggestion.marginPercent }
      if (markupPrice.value !== null && same(customerValue.value, markupPrice.value))
        return { priceSource: 'margin', marginPercent: props.customerMargin!.percent }
      return { priceSource: 'manual', marginPercent: null }
    }
    const billingName: ComputedRef<string> = computed(() => financialBillingName(props.row.match))
    // Margin as PacSort defines it: the markup on carrier cost.
    const marginText: ComputedRef<string> = computed(() => {
      if (margin.value === null) return 'Margin shows when an amount is entered'
      const percent = amount.value === null ? null : financialPercent(margin.value, amount.value)
      return `${marginNegative.value ? 'Loss' : 'Margin'} ${financialDifferenceLabel(margin.value, props.batch.currency)}${percent ? ` (${percent} on cost)` : ''}`
    })
    const roleLabel: ComputedRef<string> = computed(() => roleNames[props.signals.role])
    // The row's state, with the names the shipment detail and the invoice's charge list use.
    const reviewLabel: ComputedRef<string> = computed(() => {
      if (financialReady(props.batch, props.row)) return props.signals.billed ? 'Billed' : 'Approved'
      if (props.row.review?.status === 'excluded') return 'Excluded'
      return props.row.review?.reason ? 'Reopened' : 'To approve'
    })
    const approvalBlocker: ComputedRef<string> = computed(() => {
      if (!props.row.match) return 'Approval needs a unique PacSort shipment.'
      if (!metadataComplete.value) return `Approval needs the invoice details: ${missingMetadata.value}.`
      if (amount.value === null) return 'Approval needs a valid invoice amount. Check the original row.'
      return ''
    })
    const matchAlert: ComputedRef<string> = computed(() =>
      props.row.matchStatus === 'ambiguous'
        ? 'Several PacSort shipments share this number, so the customer is unknown. Exclude the row with a reason, or correct the shipment in PacSort and refresh matches.'
        : props.row.matchStatus === 'missing-id'
          ? 'This invoice row has no shipment number, so it cannot be linked to a customer. Exclude it with a reason if it is billed elsewhere.'
          : 'No PacSort shipment with this number was found. Check the number in PacSort, or exclude the row with a reason.',
    )
    const summaryTitle: ComputedRef<string> = computed(() => {
      if (!props.row.match) return 'Shipment match needs attention'
      if (differences.value.length) return `${differences.value.map((comparison) => comparison.label).join(' and ')} differs`
      if (blockingWarnings.value.length) return 'Check the other rows for this shipment'
      return props.signals.role === 'base' ? 'No measured difference' : `${roleLabel.value} to review`
    })
    const differsText: ComputedRef<string> = computed(() =>
      differences.value.map((comparison) => `${comparison.label}: ${comparison.difference}`).join(' · '),
    )
    // One confirmation; with a blocking check it also covers the other rows for the shipment.
    const confirmText: ComputedRef<string> = computed(() =>
      blockingWarnings.value.length
        ? 'I have checked the customer, service, weight and charge against the agreement, and the other rows ' +
          'for this shipment; it does not bill the customer twice.'
        : 'I have checked the customer, service, weight and charge against the agreement.',
    )
    const customerHint: ComputedRef<string> = computed(() =>
      customerValue.value !== null && customerValue.value !== customerPrice.value.trim()
        ? `Read as ${customerValue.value}. Use a point or a comma as decimal separator, no thousands separator.`
        : '',
    )
    const purchaseHint: ComputedRef<string> = computed(() =>
      purchaseValue.value !== null && purchaseValue.value !== purchasePrice.value.trim()
        ? `Read as ${purchaseValue.value}.`
        : '',
    )
    const dimensionsLabel: ComputedRef<string> = computed(() => {
      const size = props.row.match?.dimensionsCm
      return size ? `${size.length ?? '—'} × ${size.width ?? '—'} × ${size.height ?? '—'} cm` : 'Not booked'
    })

    const timeLabel = (value: string): string =>
      new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    const amountLabel = (value: string | null, currency = ''): string => financialAmountLabel(value, currency)
    const statusText = (status: FinancialReviewStatus): string =>
      status === 'approved' ? 'Approved' : status === 'excluded' ? 'Excluded' : 'Reopened'
    const roleText = (role: FinancialChargeRole): string => roleNames[role]
    // "Weight · 150 g · same as booked": the row header, then the invoice value and the result.
    const sameLine = (item: FinancialComparison): string =>
      `${item.invoice} · ${item.difference.charAt(0).toLowerCase()}${item.difference.slice(1)}`
    // Who decided, when, and for an approval on which price basis.
    const lastDecision: ComputedRef<string> = computed(() => {
      const review = props.row.review
      if (!review) return ''
      const basis =
        review.status !== 'approved' || !review.priceSource
          ? ''
          : review.priceSource === 'booked'
            ? ' · PacSort booked price'
            : review.priceSource === 'margin'
              ? ` · cost + ${review.marginPercent ?? '?'}% margin`
              : ' · entered by reviewer'
      const when = timeLabel(review.reviewedAt)
      return `Last decision: ${statusText(review.status)} by ${review.userName} · ${when}${basis}`
    })
    // A row that is open again: who reopened it, when and why.
    const reopened: ComputedRef<string> = computed(() => {
      const review = props.row.review
      return review?.status === 'pending' && review.reason
        ? `Reopened by ${review.userName} · ${timeLabel(review.reviewedAt)} · ${review.reason}`
        : ''
    })
    const hasChecks: ComputedRef<boolean> = computed(() => !!reopened.value || props.signals.warnings.length > 0)
    const invoiceLabel = (entry: FinancialShipmentEntry): string =>
      `${entry.batch.carrier} ${financialInvoiceLabel(entry.batch)}`
    // Each Open row names its row and invoice, as in the shipment list, so screen readers can tell them apart.
    const openLabel = (entry: FinancialShipmentEntry): string =>
      `Open row ${entry.row.rowNumber} on ${entry.batch.carrier} invoice ${financialInvoiceLabel(entry.batch)}`
    // The same status names as the shipment detail.
    const entryStatus = (entry: FinancialShipmentEntry): string => {
      if (entry.changed) return 'Changed after billing'
      if (entry.billed) return 'Billed'
      if (financialReady(entry.batch, entry.row)) return 'Approved'
      if (entry.row.review?.status === 'excluded') return 'Excluded'
      return entry.row.match ? 'To approve' : 'Not linked'
    }
    const clearErrors = (): void => {
      Object.assign(errors, { customerPrice: '', purchasePrice: '', note: '', confirmed: '' })
    }
    const load = (): void => {
      const saved = props.row.review
      const draft = props.draft
      // A row without a decision starts from its PacSort price; the reviewer still approves it.
      customerPrice.value = draft?.customerPrice ?? saved?.customerPrice ?? (saved?.status === 'excluded' ? '' : (props.suggestion?.amount ?? ''))
      purchasePrice.value = draft?.purchasePrice ?? saved?.purchasePrice ?? ''
      note.value = draft?.note ?? saved?.note ?? ''
      draftRestored.value = !!draft
      tariffOpen.value = !!purchasePrice.value
      confirmed.value = false
      clearErrors()
    }
    watch(() => props.row.id, load, { immediate: true })
    watch(
      () => props.row.review,
      () => {
        // A saved decision always needs a fresh agreement check for the next change.
        confirmed.value = false
        if (!props.draft) load()
      },
    )
    watch([() => props.batch.currency, () => props.batch.amountBasis], () => {
      confirmed.value = false
    })
    // A row without a decision follows its PacSort price, for example after the invoice's amount basis is confirmed.
    watch(
      () => props.suggestion?.amount ?? null,
      (next, previous) => {
        if (props.draft || props.row.review?.customerPrice || props.row.review?.status === 'excluded') return
        if (!customerPrice.value.trim() || customerPrice.value === previous) customerPrice.value = next ?? ''
      },
    )
    // Approving or excluding removes the approve buttons, reopening removes Reopen, and a confirmed mapping removes its
    // button. Once the save is done, focus moves to what took their place instead of falling back to the page.
    let refocus = ''
    watch(
      [() => props.row.id, decided, mappingMissing, () => props.busy],
      ([id, now, mapping, busy], [before, was, wasMapping]) => {
        if (id !== before) refocus = ''
        else if (now !== was) refocus = now ? 'financial-review-reopen' : 'financial-customer-price'
        else if (wasMapping && !mapping) refocus = 'financial-summary-title'
        if (!refocus || busy) return
        const lost = !document.activeElement || document.activeElement === document.body
        if (lost) document.getElementById(refocus)?.focus()
        refocus = ''
      },
      { flush: 'post' },
    )
    const toggleTariff = (event: Event): void => {
      tariffOpen.value = (event.target as HTMLDetailsElement).open
    }
    const saveDraft = (): void => {
      const saved = props.row.review
      const start = saved?.customerPrice ?? (saved?.status === 'excluded' ? '' : (props.suggestion?.amount ?? ''))
      const unchanged =
        customerPrice.value === start &&
        purchasePrice.value === (saved?.purchasePrice ?? '') &&
        note.value === (saved?.note ?? '')
      emit('draft', unchanged ? null : { customerPrice: customerPrice.value, purchasePrice: purchasePrice.value, note: note.value })
    }
    // Corrected input removes its error right away; the draft keeps the value while navigating.
    const edited = (field: 'customerPrice' | 'purchasePrice' | 'note'): void => {
      errors[field] = ''
      saveDraft()
    }
    const useSuggestion = (): void => {
      if (!props.suggestion) return
      customerPrice.value = props.suggestion.amount
      edited('customerPrice')
    }
    const useMarkup = (): void => {
      if (markupPrice.value === null) return
      customerPrice.value = markupPrice.value
      edited('customerPrice')
    }
    const discardDraft = (): void => {
      emit('draft', null)
      draftRestored.value = false
      const saved = props.row.review
      customerPrice.value = saved?.customerPrice ?? (saved?.status === 'excluded' ? '' : (props.suggestion?.amount ?? ''))
      purchasePrice.value = props.row.review?.purchasePrice ?? ''
      note.value = props.row.review?.note ?? ''
      confirmed.value = false
      clearErrors()
    }
    const focusFirstError = (): void => {
      // The tariff field sits in a folded section; it opens so its error can take focus.
      if (errors.purchasePrice) tariffOpen.value = true
      nextTick(() => {
        const selector = errors.customerPrice
          ? '#financial-customer-price'
          : errors.purchasePrice
            ? '#financial-purchase-price'
            : errors.note
              ? '#financial-review-note'
              : '.financial__decision input[type="checkbox"][aria-invalid="true"]'
        document.querySelector<HTMLElement>(selector)?.focus()
      })
    }
    const submit = (status: FinancialReviewStatus, next = false): void => {
      // A decided row only reopens; Enter in one of its read-only fields does not approve it again.
      if (decided.value && status !== 'pending') return
      clearErrors()
      if (status === 'approved') {
        if (customerValue.value === null)
          errors.customerPrice = customerPrice.value.trim()
            ? 'Enter the amount as a number, for example 26.50.'
            : 'Enter the customer amount from the agreement.'
        if (purchasePrice.value.trim() && purchaseValue.value === null)
          errors.purchasePrice = 'Enter the tariff as a number, or leave it empty.'
        if (noteRequired.value && !note.value.trim())
          errors.note = 'Explain the difference, warning, credit or negative margin before approving.'
        if (!confirmed.value)
          errors.confirmed = blockingWarnings.value.length
            ? 'Confirm that you have checked the row against the agreement and the other rows for this shipment.'
            : 'Confirm that you have checked the row against the agreement.'
      }
      if (status === 'excluded' && !note.value.trim()) errors.note = 'Add the reason for excluding this row.'
      if (Object.values(errors).some(Boolean)) {
        focusFirstError()
        return
      }
      const submission: FinancialReviewSubmission = {
        status,
        customerPrice: status === 'excluded' ? '' : (customerValue.value ?? customerPrice.value.trim()),
        purchasePrice: purchaseValue.value ?? '',
        note: note.value.trim(),
        // The one confirmation covers the other rows when a check blocks.
        warningsChecked: confirmed.value && blockingWarnings.value.length > 0,
        next,
        ...(status === 'approved' ? priceSource() : { priceSource: 'manual' as const, marginPercent: null }),
      }
      emit('review', submission)
    }
    const approve = (): void => submit('approved')
    const approveNext = (): void => submit('approved', true)
    const exclude = (): void => submit('excluded')
    const reopen = (): void => submit('pending')
    const editInvoice = (): void => {
      emit('invoice-details')
    }
    const close = (): void => {
      emit('close')
    }
    const confirmMapping = (): void => {
      emit('confirm-mapping', props.row.description, props.row.match?.service ?? '')
    }
    const openEntry = (entry: FinancialShipmentEntry): void => {
      emit('open-entry', entry.batch.id, entry.row.id)
    }
    return {
      customerPrice,
      purchasePrice,
      note,
      confirmed,
      draftRestored,
      tariffOpen,
      errors,
      amount,
      customerValue,
      purchaseValue,
      metadataComplete,
      missingMetadataList,
      differences,
      booked,
      checkRows,
      mappingMissing,
      hasMissing,
      reopened,
      hasChecks,
      blockingWarnings,
      decided,
      marginNegative,
      noteRequired,
      tariffDelta,
      marginText,
      markupPrice,
      useMarkup,
      same,
      billingName,
      useSuggestion,
      roleLabel,
      reviewLabel,
      approvalBlocker,
      matchAlert,
      summaryTitle,
      differsText,
      confirmText,
      customerHint,
      purchaseHint,
      dimensionsLabel,
      timeLabel,
      amountLabel,
      statusText,
      roleText,
      sameLine,
      lastDecision,
      invoiceLabel,
      openLabel,
      entryStatus,
      toggleTariff,
      edited,
      discardDraft,
      approve,
      approveNext,
      exclude,
      reopen,
      editInvoice,
      close,
      confirmMapping,
      openEntry,
    }
  },
})
</script>
